// The branch graph column (spec/dashboard.md section 12.1): an SVG next to the commit list — a dot per commit on its
// branch's lane, a straight edge to a parent on the same lane, a diagonal one across lanes (a merge or a branch point),
// the current branch's lane highlighted; the row height matches the list's rows so the two stay aligned.
import { useMemo } from 'react';
import { activeLanesBelow, type GraphRow } from './graph';

// the lanes take the token colours (both themes derive them), the first lane the accent: it is the current branch
const LANE_COLOURS = [
  'var(--hue-accent)',
  'var(--hue-warning)',
  'var(--hue-success)',
  'var(--hue-info)',
  'var(--hue-danger)',
  'var(--hue-text-muted)',
  'color-mix(in srgb, var(--hue-info) 55%, var(--hue-success))',
  'color-mix(in srgb, var(--hue-warning) 55%, var(--hue-danger))',
];

export function laneColour(lane: number): string {
  return LANE_COLOURS[lane % LANE_COLOURS.length]!;
}

export interface BranchGraphProps {
  rows: GraphRow[];
  lanes: string[];
  rowHeight: number;
  laneWidth?: number;
  /** the lanes drawn (the others collapse): "Current branch only" passes the first lane */
  visibleLanes?: Set<number>;
  selected?: string | null;
  onSelect?: (sha: string) => void;
}

export function BranchGraph({ rows, lanes, rowHeight, laneWidth = 18, visibleLanes, selected, onSelect }: BranchGraphProps) {
  const shown = useMemo(() => (visibleLanes ? lanes.map((_, i) => i).filter((i) => visibleLanes.has(i)) : lanes.map((_, i) => i)), [lanes, visibleLanes]);
  const x = (lane: number) => (shown.indexOf(lane) < 0 ? -100 : shown.indexOf(lane) * laneWidth + laneWidth / 2);
  const y = (row: number) => row * rowHeight + rowHeight / 2;
  const width = Math.max(1, shown.length) * laneWidth;
  const height = rows.length * rowHeight;
  const visibleRows = rows.map((r, i) => [r, i] as const).filter(([r]) => !visibleLanes || visibleLanes.has(r.lane));
  return (
    <svg className="branch-graph" width={width} height={height} role="img" aria-label={`Branch graph: ${lanes.length} lane${lanes.length === 1 ? '' : 's'}`} data-testid="branch-graph">
      {rows.map((r, i) =>
        r.parents.map((p) => {
          if (visibleLanes && (!visibleLanes.has(r.lane) || !visibleLanes.has(p.lane))) return null;
          const x1 = x(r.lane);
          const y1 = y(i);
          const x2 = x(p.lane);
          const y2 = y(p.row);
          const d = x1 === x2 ? `M${x1},${y1} L${x2},${y2}` : `M${x1},${y1} C${x1},${y1 + rowHeight / 2} ${x2},${y2 - rowHeight / 2} ${x2},${y2}`;
          return <path key={`${r.sha}-${p.row}`} d={d} fill="none" stroke={laneColour(p.lane === r.lane ? r.lane : p.lane)} strokeWidth={1.5} data-edge={p.lane === r.lane ? 'straight' : 'cross'} />;
        }),
      )}
      {rows.map((r, i) => {
        // the lanes still alive below this row keep a line through it
        if (visibleLanes && !visibleLanes.has(r.lane)) return null;
        return Array.from(activeLanesBelow(rows, i))
          .filter((lane) => lane !== r.lane && (!visibleLanes || visibleLanes.has(lane)))
          .map((lane) => <line key={`${r.sha}-lane-${lane}`} x1={x(lane)} y1={y(i) - rowHeight / 2} x2={x(lane)} y2={y(i) + rowHeight / 2} stroke={laneColour(lane)} strokeWidth={1.5} opacity={0.6} />);
      })}
      {visibleRows.map(([r, i]) => (
        <g key={r.sha} data-testid={`graph-node-${r.sha}`} data-lane={r.lane} onClick={() => onSelect?.(r.sha)} className={onSelect ? 'graph-node clickable' : 'graph-node'}>
          <circle cx={x(r.lane)} cy={y(i)} r={r.merge ? 5 : 4} fill={r.heads.length > 0 ? laneColour(r.lane) : 'var(--hue-surface)'} stroke={laneColour(r.lane)} strokeWidth={r.sha === selected ? 3 : 1.5} />
          {r.heads.length > 0 && <title>{r.heads.join(', ')}</title>}
        </g>
      ))}
    </svg>
  );
}
